import { useMemo } from 'react';
import { StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Chip } from '@vnetwork/ui/primitives';
import type { CustomerTicketRow } from '@/api/customer-tickets';
import { TableLink } from '@/components/table-link';
import { EntityLink } from '@/components/entity-link';
import { formatDate } from '@/lib/format';
import { humanize } from '../status-tones';
import { ticketPriorityTone, ticketStatusTone, ticketTypeTone } from './ticket-tones';

/**
 * Ticket TYPE — the leading English segment of the bilingual `ticket_type_title`
 * ("CREATE SERVICE - Tạo mới Dịch vụ" → "Create Service"), or the ticket source.
 * Rendered as a Chip. Returns '' when unknown.
 */
function ticketType(row: CustomerTicketRow): string {
  const title = row.ticket_type_title?.trim();
  // Lowercase first so humanize yields Title Case ("CREATE SERVICE" → "Create Service").
  if (title) return humanize(title.split(' - ')[0].toLowerCase());
  if (row.ticket_source) return humanize(row.ticket_source.toLowerCase());
  return '';
}

/** Rating cell: "4.5 (3)" from avg_rating + feedback_count, or "—" when unrated. */
function ratingText(row: CustomerTicketRow): string {
  if (typeof row.avg_rating !== 'number' || row.avg_rating <= 0) return '—';
  const count = row.feedback_count ?? 0;
  return count > 0 ? `${row.avg_rating.toFixed(1)} (${count})` : row.avg_rating.toFixed(1);
}

export function useTicketColumns() {
  return useMemo<ColumnDef<CustomerTicketRow, unknown>[]>(
    () => [
      {
        id: 'ticket',
        header: 'ID',
        size: 56,
        cell: ({ row }) => (
          <TableLink to={`/tickets/${row.original.ticket_id}`}>{row.original.ticket_id}</TableLink>
        ),
      },
      {
        id: 'service',
        header: 'Service',
        size: 260,
        cell: ({ row }) => {
          const { service_id, service_name } = row.original;
          if (!service_id && !service_name) return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
          return (
            <EntityLink
              to={`/services/${service_id}`}
              id={service_id!}
              name={service_name}
            />
          );
        },
      },
      {
        id: 'ticket_type',
        header: 'Type',
        cell: ({ row }) => {
          const type = ticketType(row.original);
          return type ? (
            <Chip tone={ticketTypeTone(type)} size="sm">
              {type}
            </Chip>
          ) : (
            <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>
          );
        },
      },
      {
        id: 'ticket_status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusPill tone={ticketStatusTone(row.original.ticket_status)}>
            {humanize(row.original.ticket_status)}
          </StatusPill>
        ),
      },
      {
        id: 'ticket_priority',
        header: 'Priority',
        cell: ({ row }) => (
          <Chip tone={ticketPriorityTone(row.original.ticket_priority)} size="sm">
            {humanize(row.original.ticket_priority)}
          </Chip>
        ),
      },
      {
        id: 'employee_name',
        header: 'Assignee',
        cell: ({ row }) => row.original.employee_name || '—',
      },
      {
        id: 'ticket_requested_at',
        header: 'Requested',
        cell: ({ row }) => formatDate(row.original.ticket_requested_at),
      },
      {
        id: 'ticket_estimated_completion_date',
        header: 'Est. completion',
        cell: ({ row }) => formatDate(row.original.ticket_estimated_completion_date),
      },
      {
        id: 'rating',
        header: () => <div style={{ textAlign: 'right' }}>Rating</div>,
        cell: ({ row }) => <div style={{ textAlign: 'right' }}>{ratingText(row.original)}</div>,
      },
    ],
    [],
  );
}
